import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'md' | 'sm';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover shadow-sm',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-alt',
  ghost: 'bg-transparent text-ink-secondary hover:bg-primary-soft hover:text-primary',
  danger: 'bg-danger text-white hover:brightness-95',
};

// PRD: target sentuh minimal 44px di mobile. Desktop boleh sedikit lebih ringkas.
const SIZES: Record<Size, string> = {
  md: 'min-h-11 px-5 text-[15px] sm:min-h-10',
  sm: 'min-h-11 px-3.5 text-sm sm:min-h-9',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  className,
  children,
  disabled,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] font-medium',
        'transition-[box-shadow,background-color,transform] duration-150 ease-out',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
